import { machineNameOf } from './machineName'
import { ownerDisplayName } from '../chat/rosterGroups'
import type { AgentRow, ProjectMember } from '../transport/api'

/**
 * WHAT A `ping-agent:` TAG SAYS, IN WORDS A PERSON READS.
 *
 * Owner, 2026-09-21, with a screenshot of a Project Chat message showing a bare
 * blue `ping-agent:f60fca384841:yy-secretary`: 「把這個 ping agent 的 tag 在
 * CleanView、Project Chat 還有 Friend Chat 的時候可以解析成為一個…字,讓他標示這一個
 * Agent 的名稱 at 機器。但如果假設這個 agent 不是自己的,是別的朋友的 Agent,那麼就讓
 * 這個標注成為 Agent 然後後面是 Owner」.
 *
 * So the tag resolves to `name@where`, and WHERE depends on whose agent it is:
 *
 *   mine        the MACHINE it runs on — that is the thing I can act on, and it
 *               is what every other surface in this app already says about my
 *               own agents (machineName.ts holds that one rule).
 *   a friend's  the OWNER. Naming their machine would be both useless to me and
 *               a small leak of their setup; the useful fact is whose agent it
 *               is. This is the same choice the @-picker already made for a
 *               room's agents (AgentMention.mentionRowsOfMembers).
 *
 * ── THE LABEL IN THE TAG IS A HINT, NOT THE ANSWER ──────────────────────────
 *
 * `pingTag()` writes `session@host` when the agent was on a mounted remote and
 * bare `session` when it was local — LOCAL TO WHOEVER WROTE THE TAG. The owner's
 * screenshot is exactly that case: `yy-secretary` with no machine, because the
 * person who sent it was on the machine it runs on. For every other reader that
 * omission is the interesting part, so the hash is resolved against what this
 * client actually knows and the label is only the fallback.
 */
export type PingDescription = {
  /** The agent's own name. */
  name: string
  /** The machine (mine) or the owner (a friend's); '' when nothing knows. */
  at: string
  /** What to show: `name@at`, or just `name` when `at` is empty. */
  text: string
  /** Only my own agents can be opened — see openable. */
  mine: boolean
  /** The session and host a jump needs; null unless this is my own agent. */
  open: { session: string; host: string | null } | null
}

/**
 * Split a tag's label into the two things it may carry.
 *
 * `session@host`, or a bare session. An agent's session name cannot contain '@'
 * (the edge's own naming), so the LAST '@' is the separator and everything
 * before it is the session — a host with an '@' in it would otherwise eat part
 * of the name.
 */
export function splitPingLabel(label: string): { session: string; host: string | null } {
  const s = label.trim()
  if (!s) return { session: '', host: null }
  const at = s.lastIndexOf('@')
  if (at <= 0 || at === s.length - 1) return { session: s, host: null }
  return { session: s.slice(0, at).trim(), host: s.slice(at + 1).trim() || null }
}

/**
 * Resolve one tag against what this client knows.
 *
 * `roster` is my own agents (the fan-out's rows). `members` is the room's, when
 * there is a room — it is what turns an unknown hash into a person's name rather
 * than a bare hash.
 */
function describePing(
  hash: string,
  label: string,
  ctx: { roster?: AgentRow[]; members?: ProjectMember[]; localName?: string | null } = {},
): PingDescription {
  const hinted = splitPingLabel(label)
  const own = (ctx.roster ?? []).find((a) => a.hash && a.hash === hash)

  if (own) {
    const name = own.session || hinted.session || hash.slice(0, 8)
    return {
      name,
      at: machineNameOf(own, ctx.localName ?? null),
      text: '',
      mine: true,
      open: { session: own.session, host: own.host ?? null },
    }
  }

  /*
   * NOT IN MY ROSTER. That is not the same as "not mine" — my own roster can be
   * mid-fetch, or the machine can be off — so the room is asked before anything
   * is concluded. A member row that says `mine: true` is the account's own word
   * and outranks my local list either way.
   */
  const member = (ctx.members ?? []).find((m) => m.channel_hash === hash)
  if (member) {
    const raw = member.label ?? ''
    const fromMember = splitPingLabel(raw.replace(/\s+@\s+/, '@'))
    const name = fromMember.session || hinted.session || hash.slice(0, 8)
    if (member.mine === true) {
      // Mine, but this client cannot see it right now — name the machine the
      // room knows and leave it unopenable rather than jumping nowhere.
      return { name, at: fromMember.host ?? hinted.host ?? '', text: '', mine: false, open: null }
    }
    return {
      name,
      at: ownerDisplayName(ctx.members ?? [], member) ?? '',
      text: '',
      mine: false,
      open: null,
    }
  }

  // Nothing knows it: show what the tag itself carried rather than a bare hash.
  return {
    name: hinted.session || hash.slice(0, 8),
    at: hinted.host ?? '',
    text: '',
    mine: false,
    open: null,
  }
}

/** `name@at`, or just the name when nothing knows where it is. */
function pingText(d: Pick<PingDescription, 'name' | 'at'>): string {
  return d.at ? `${d.name}@${d.at}` : d.name
}

/** The whole answer, text included — what every surface actually calls. */
export function resolvePing(
  hash: string,
  label: string,
  ctx: Parameters<typeof describePing>[2] = {},
): PingDescription {
  const d = describePing(hash, label, ctx)
  return { ...d, text: pingText(d) }
}
